import _ from "lodash";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { useHistory } from "react-router-dom";
import {
  CurrencyContext,
  StyleTypeContext,
  TranslationContext,
} from "../../../umbilical_tools";
import PropTypes from "prop-types";
import jssVariables from "../../../jss_setup";
import Stepper from "../../common/Stepper";
import { useBookingProcess } from "../../../hooks/booking";
import { useDispatch, useSelector } from "react-redux";
import {
  initPayment,
  onCarRentalPreBook,
  validateCarRentalDriverData,
} from "./helpers";
import { selectedCarRentalChange } from "../../../actions/search/car_rental_search";
import Prebook from "../Prebook";
import { Button } from "../../common/Button";
import TravellersForms from "../TravellersForms";
import { carRentalBook } from "../../../api/carRental";
import { v4 } from "uuid";
import { useApoAuth, useBaseReservationPayload } from "../../../hooks";
import { useCarRentalsGetSessionMeta } from "../../../hooks/carRental";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";

const styles = createUseStyles({
  bookingProcess: (props) => ({
    position: "fixed",
    height: "100%",
    width: "100vw",
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    background: jssVariables.colors.background.light,
    padding: jssVariables.normal_gap,
    justifyContent: "center",
    zIndex: 9999,
    ..._.get(props, "customStyles.bookingProcess", {}),
  }),
  content: (props) => ({
    boxShadow: jssVariables.shadows.normalShadow,
    borderRadius: "10px",
    padding: `calc(${jssVariables.normal_gap} * 1.5)`,
    height: "max-content",
    maxHeight: "70vh",
    minWidth: "90vw",
    overflowY: "auto",
    background: "white",
    ..._.get(props, "customStyles.content", {}),
  }),
  actions: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    justifyContent: "end",
    gridGap: `calc(${jssVariables.normal_gap} * 2)`,
    paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.actions", {}),
  }),
  placeholderBtn: (props) => ({
    height: "2rem!important",
    width: "6rem!important",
    ..._.get(props, "customStyles.placeholderBtn", {}),
  }),
  loadingIconContr: (props) => ({
    display: "grid",
    placeContent: "center",
    height: "100%",
    ..._.get(props, "customStyles.loadingIconContr", {}),
  }),
  loadingIcon: (props) => ({
    fontSize: "xxx-large",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.loadingIcon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    content: (props) => ({
      minWidth: "50rem",
      maxWidth: "50rem",
      height: "max-content",
      maxHeight: "75vh",
      marginTop: `calc(${jssVariables.normal_gap} * 2)`,
      padding: `${jssVariables.double_gap} calc(${jssVariables.normal_gap} * 3)`,

      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
    actions: (props) => ({
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      justifyContent: "end",
      gridGap: `calc(${jssVariables.normal_gap} * 2)`,
      paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
      ..._.get(props, "customStyles.actions.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
const CarRentalBookingProcess = ({ page_session_id }) => {
  const [preBookResult, setPreBookResult] = useState({});
  const [driver, setDriver] = useState({});
  const [agreed, setAgreed] = useState(false);

  const { source_entity, market_type } = useApoAuth();

  const { search_session_id } = useCarRentalsGetSessionMeta({
    page_session_id,
  });

  const { t } = useContext(TranslationContext);
  const { currency: settedCurrency } = useContext(CurrencyContext);
  const history = useHistory();
  const dispatch = useDispatch();
  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const classes = styles({
    customStyles: cStyler({
      type,
      brand,
      objName: "CarRentalBookingProcess",
    }),
  });

  const {
    payment_supplier,
    loading,
    step,
    onIncreaseStep,
    onDecreaseStep,
    onIdle,
    onLoading,
  } = useBookingProcess({ initialLoading: false });

  const { carRental, website_meta } = useSelector((state) => {
    const { api_key, name } = state.WebsiteMetadata.metadata;

    return {
      carRental: _.get(state.CarRentalSelected, "car_rental", null),
      website_meta: { api_key, name },
    };
  });

  function onBackToCarRentalList() {
    history.push(`/car-rentals/${page_session_id}`);
  }

  const onPrebookData = useCallback(
    ({ key, value }) => {
      dispatch(selectedCarRentalChange({ key, value }));
    },
    [dispatch]
  );

  // Prebook;
  useEffect(() => {
    if (_.isEmpty(carRental)) {
      onBackToCarRentalList();
    } else {
      onLoading();
      onCarRentalPreBook({
        carRental,
        search_session_id,
        market_type,
        source_entity,
        onPrebookData,
        onCancel: onBackToCarRentalList,
        t,
      }).then((result) => {
        setPreBookResult(result);
        onIdle();
      });
    }
  }, []);

  const { first_name, last_name, email, customer_uid } =
    useBaseReservationPayload({
      group_leader: {
        ...driver,
      },
    });

  async function onBook() {
    const guestPayload = {
      title: driver.title,
      gender: driver.gender,
      birth_date: driver.date_of_birth[0],
      first_name: driver.first_name,
      last_name: driver.last_name,
      phone_number: driver.phone,
      email: driver.email,
      street: driver.address,
      city_name: driver.city,
      postal_code: driver.postal_code,
      country_code: driver.country,
    };

    const customer_user_data = {
      customer_uid: customer_uid.customer_uid
        ? customer_uid.customer_uid
        : v4(),
      first_name,
      last_name,
      email: email,
    };

    const bookPayload = {
      ...guestPayload,
      supplier: preBookResult.supplier,
      customer_remarks: "",
      // ===============================================================
      session_id: search_session_id,
      booking_id: preBookResult.booking_id,
      language: "EN",
      api_uid: preBookResult.api_uid,
      website_meta,
      source_entity,
      market_type,
      guests: [
        {
          ...guestPayload,
          guest_type: "ADT",
          group_leader: true,
          gender: driver.gender === "male" ? "MA" : "FE",
        },
      ],
      customer_user_data,
    };

    const response = await carRentalBook(bookPayload);

    if (!response) {
      history.push(`/failed-booking/${page_session_id}`);
      return;
    }

    const initialized = await initPayment({
      payload: {
        customer_user_data,
        page_session_id,
        settedCurrency,
        source_entity,
        amount: _.get(response, "booking_result.payment_data.amount"),
        currency: _.get(response, "booking_result.payment_data.currency"),
        reference: _.get(response, "booking_result.reference"),
      },
      dispatch,
      // selectionData,
    });

    if (!initialized) {
      history.push(`/failed-booking/${page_session_id}`);
      return;
    }
  }

  useEffect(() => {
    if (step !== 2) {
      return;
    }
    onBook();
  }, [step]);

  return (
    <div className={classes.bookingProcess}>
      <div className={classes.stepper}>
        <Stepper
          steps={[t("prebook"), t("driverInfo"), t("book")]}
          current={step === 4 ? 2 : step}
        />
      </div>
      <div className={classes.content}>
        <React.Fragment>
          {step === 0 && (
            <Prebook
              loading={loading}
              preBookResult={preBookResult}
              agreed={agreed}
              setAgreed={setAgreed}
            />
          )}
          {step === 1 && <TravellersForms setDriver={setDriver} />}
          {step === 2 && (
            <div className={classes.loadingIconContr}>
              <Icon icon="eos-icons:loading" className={classes.loadingIcon} />
            </div>
          )}
        </React.Fragment>
      </div>
      <div className={classes.actions}>
        <Button
          id={step > 0 ? "previousStep" : "cancel"}
          ghost={loading}
          text={step > 0 ? t("previousStep") : t("cancel")}
          bold={true}
          size="large"
          onClick={() =>
            step > 0 ? onDecreaseStep() : onBackToCarRentalList()
          }
        />
        <Button
          id={step < 2 ? "nextStep" : "payment"}
          ghost={loading}
          text={step < 2 ? t("nextStep") : t("payment")}
          bold={true}
          size="large"
          onClick={async () => {
            if (step === 0 && !agreed) {
              toast.warning(t("pleaseCheckTheBox"));
              return;
            }
            if (step === 1) {
              const isValid = await validateCarRentalDriverData(driver, t);
              if (!isValid) return;
              onIncreaseStep();
            } else {
              onIncreaseStep();
            }
          }}
        />
      </div>
    </div>
  );
};
CarRentalBookingProcess.defaultProps = {
  page_session_id: false,
};
CarRentalBookingProcess.propTypes = {
  page_session_id: PropTypes.string.isRequired,
};
export default CarRentalBookingProcess;
